import { useApolloClient, useSubscription } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { notification } from 'antd'
import { Worklog, WorklogOrderByInput, WorklogWhereInput } from '../../../generated/graphql'
import { WORKLOG_CHANGED_SUBSCRIPTION, WORKLOGS } from '../gql/verteOfficeGQL'
import { ACTIVE_WORKLOG_ORDER_BY, resolveActiveWorklog } from '../shared/resolveActiveWorklog'

interface WorklogOutputType {
  worklogs: Worklog[]
}

interface WorklogInputType {
  where: WorklogWhereInput
  orderBy: WorklogOrderByInput[]
}

export function useActiveWorklog(userId?: string) {
  const client = useApolloClient()
  const [activeWorklog, setActiveWorklog] = useState<Worklog | undefined>()
  const [loading, setLoading] = useState(false)
  // Prevents "while you were gone" toast from firing when the subscription already showed a real-time one
  const suppressGoneToastRef = useRef(false)
  const lastActiveWorklogIdRef = useRef<string | undefined>()

  const refresh = useCallback(async () => {
    setLoading(true)

    try {
      const resolved = await resolveActiveWorklog({
        userId,
        fetchWorklogs: async (where) => {
          const result = await client.query<WorklogOutputType, WorklogInputType>({
            query: WORKLOGS,
            variables: {
              where,
              orderBy: ACTIVE_WORKLOG_ORDER_BY,
            },
            fetchPolicy: 'no-cache',
          })

          return result.data?.worklogs ?? []
        },
      })

      if (lastActiveWorklogIdRef.current && !resolved && !suppressGoneToastRef.current) {
        notification.info({ message: 'Tu actividad fue cerrada mientras no estabas en la plataforma' })
      }
      suppressGoneToastRef.current = false

      lastActiveWorklogIdRef.current = resolved?.id
      setActiveWorklog(resolved as Worklog | undefined)
    } catch (error) {
      console.error('Error resolving active worklog:', error)
      lastActiveWorklogIdRef.current = undefined
      setActiveWorklog(undefined)
    } finally {
      setLoading(false)
    }
  }, [client, userId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Real-time: backend closes the worklog (cron) → subscription fires → instant toast + refresh
  const { data: subscriptionData } = useSubscription(WORKLOG_CHANGED_SUBSCRIPTION)

  useEffect(() => {
    if (!subscriptionData?.worklogChanged) return
    const changed = subscriptionData.worklogChanged
    if (changed.user_id !== userId) return

    if (activeWorklog?.id === changed.id && changed.end_time != null) {
      suppressGoneToastRef.current = true
      notification.warning({ message: 'Tu actividad fue finalizada automáticamente' })
    }
    void refresh()
  }, [subscriptionData, userId, refresh, activeWorklog?.id])

  return {
    activeWorklog,
    loading,
    refresh,
  }
}
